import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { jsonToToon, toonToJson, formatJson, validateJson } from './utils/converter';
import { samples } from './utils/samples';
import SEOMetadata from './components/SEOMetadata';
import Features from './components/Features';
import HowToUse from './components/HowToUse';
import FAQ from './components/FAQ';
import AboutTOON from './components/AboutTOON';
import logo from './images/jsontoon.png';
import './App.css';

type Theme = 'light' | 'dark';

function HomePage() {
  const [jsonInput, setJsonInput] = useState(samples[0].json);
  const [toonInput, setToonInput] = useState('');
  const [theme, setTheme] = useState<Theme>('dark');
  const [copyFeedback, setCopyFeedback] = useState<'json' | 'toon' | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Apply theme to document
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Validation
  const jsonValidation = useMemo(() => {
    if (!jsonInput.trim()) return null;
    return validateJson(jsonInput);
  }, [jsonInput]);

  // Handlers
  const handleJsonChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonInput(e.target.value);
  }, []);

  const handleToonChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setToonInput(e.target.value);
  }, []);

  const handleSampleLoad = useCallback((sampleJson: string) => {
    setJsonInput(sampleJson);
    setToonInput('');
  }, []);

  const convertJsonToToon = useCallback(() => {
    if (jsonInput.trim()) {
      const result = jsonToToon(jsonInput);
      if (!result.error) {
        setToonInput(result.toon);
      }
    }
  }, [jsonInput]);

  const convertToonToJson = useCallback(() => {
    if (toonInput.trim()) {
      const result = toonToJson(toonInput);
      if (!result.error) {
        setJsonInput(result.toon);
      }
    }
  }, [toonInput]);

  const handleSwap = useCallback(() => {
    const temp = jsonInput;
    setJsonInput(toonInput);
    setToonInput(temp);
  }, [jsonInput, toonInput]);

  const handleCopyJson = useCallback(async () => {
    if (jsonInput) {
      await navigator.clipboard.writeText(jsonInput);
      setCopyFeedback('json');
      setTimeout(() => setCopyFeedback(null), 2000);
    }
  }, [jsonInput]);

  const handleCopyToon = useCallback(async () => {
    if (toonInput) {
      await navigator.clipboard.writeText(toonInput);
      setCopyFeedback('toon');
      setTimeout(() => setCopyFeedback(null), 2000);
    }
  }, [toonInput]);

  const handleDownloadJson = useCallback(() => {
    if (jsonInput) {
      const blob = new Blob([jsonInput], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'data.json';
      a.click();
      URL.revokeObjectURL(url);
    }
  }, [jsonInput]);

  const handleDownloadToon = useCallback(() => {
    if (toonInput) {
      const blob = new Blob([toonInput], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'data.toon';
      a.click();
      URL.revokeObjectURL(url);
    }
  }, [toonInput]);

  const handleFormatJson = useCallback(() => {
    if (jsonInput) {
      setJsonInput(formatJson(jsonInput));
    }
  }, [jsonInput]);

  const handleClearAll = useCallback(() => {
    setJsonInput('');
    setToonInput('');
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  }, []);

  const handleDropdownSelect = useCallback((sampleJson: string) => {
    setJsonInput(sampleJson);
    setToonInput('');
    setDropdownOpen(false);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (dropdownOpen && !target.closest('.custom-dropdown')) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  // Calculate stats
  const stats = useMemo(() => {
    if (jsonInput.trim() && toonInput.trim()) {
      const jsonTokens = Math.ceil(jsonInput.length / 4);
      const toonTokens = Math.ceil(toonInput.length / 4);
      const savingsPercent = Math.round(((jsonTokens - toonTokens) / jsonTokens) * 100);
      return {
        jsonTokens,
        toonTokens,
        savingsPercent: Math.max(0, savingsPercent)
      };
    }
    return null;
  }, [jsonInput, toonInput]);

  return (
    <div className="app">
      <SEOMetadata />

      {/* Header */}
      <header className="header">
        <div className="header-content">
          <div className="logo">
            <img src={logo} alt="JSON to TOON Logo" className="logo-image" />
            <h1>JSON ↔ TOON</h1>
          </div>
          <p className="tagline">
            Reduce LLM token usage by 30-60% • Fast • Private • Free
          </p>
          <button
            className="theme-toggle"
            onClick={toggleTheme}
            aria-label="Toggle theme"
          >
            {theme === 'light' ? '🌙' : '☀️'}
          </button>
        </div>
      </header>

      <AboutTOON />

      {/* Main Content */}
      <main className="main" id="converter">
        {/* Controls */}
        <div className="controls">
          {/* Desktop: Sample Buttons */}
          <div className="sample-buttons desktop-only">
            <span className="sample-label">Load sample:</span>
            {samples.map((sample, idx) => (
              <button
                key={idx}
                className="sample-btn"
                onClick={() => handleSampleLoad(sample.json)}
                title={sample.description}
              >
                {sample.name}
              </button>
            ))}
          </div>

          {/* Mobile: Custom Dropdown */}
          <div className="sample-dropdown mobile-only">
            <span className="sample-label">Load sample:</span>
            <div className="custom-dropdown">
              <button
                className="dropdown-trigger"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                aria-expanded={dropdownOpen}
                aria-haspopup="listbox"
              >
                <span>Select a sample</span>
                <span className={`dropdown-arrow ${dropdownOpen ? 'open' : ''}`}>▼</span>
              </button>
              {dropdownOpen && (
                <div className="dropdown-menu" role="listbox">
                  {samples.map((sample, idx) => (
                    <button
                      key={idx}
                      className="dropdown-item"
                      onClick={() => handleDropdownSelect(sample.json)}
                      role="option"
                      aria-selected={false}
                      title={sample.description}
                    >
                      {sample.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="action-buttons">
            <button className="btn-secondary" onClick={handleFormatJson} disabled={!jsonInput}>
              Format JSON
            </button>
            <button className="btn-secondary" onClick={handleClearAll}>
              Clear All
            </button>
          </div>
        </div>

        {/* Editor Panels */}
        <div className="editor-container">
          {/* JSON Panel */}
          <div className="panel json-panel">
            <div className="panel-header">
              <h3>JSON</h3>
              {jsonValidation && !jsonValidation.valid && (
                <span className="error-badge">⚠ {jsonValidation.error}</span>
              )}
              <div className="output-actions">
                <button
                  className="btn-icon"
                  onClick={handleCopyJson}
                  title="Copy JSON"
                  disabled={!jsonInput}
                >
                  {copyFeedback === 'json' ? '✓ Copied!' : '📋'}
                </button>
                <button
                  className="btn-icon"
                  onClick={handleDownloadJson}
                  title="Download JSON"
                  disabled={!jsonInput}
                >
                  ⬇
                </button>
              </div>
            </div>
            <textarea
              className={`editor ${jsonValidation && !jsonValidation.valid ? 'error' : ''}`}
              value={jsonInput}
              onChange={handleJsonChange}
              placeholder='Paste or edit JSON here...\n\nExample:\n{\n  "name": "John",\n  "age": 30\n}'
              spellCheck={false}
            />
          </div>

          {/* Conversion Controls */}
          <div className="conversion-controls">
            <button
              className="convert-btn convert-right"
              onClick={convertJsonToToon}
              disabled={!jsonInput || (jsonValidation !== null && !jsonValidation.valid)}
              title="Convert JSON to TOON"
            >
              →
            </button>
            <button
              className="swap-btn"
              onClick={handleSwap}
              disabled={!jsonInput && !toonInput}
              title="Swap content"
            >
              ⇄
            </button>
            <button
              className="convert-btn convert-left"
              onClick={convertToonToJson}
              disabled={!toonInput}
              title="Convert TOON to JSON"
            >
              ←
            </button>
          </div>

          {/* TOON Panel */}
          <div className="panel toon-panel">
            <div className="panel-header">
              <h3>TOON</h3>
              <div className="output-actions">
                <button
                  className="btn-icon"
                  onClick={handleCopyToon}
                  title="Copy TOON"
                  disabled={!toonInput}
                >
                  {copyFeedback === 'toon' ? '✓ Copied!' : '📋'}
                </button>
                <button
                  className="btn-icon"
                  onClick={handleDownloadToon}
                  title="Download TOON"
                  disabled={!toonInput}
                >
                  ⬇
                </button>
              </div>
            </div>
            <textarea
              className="editor"
              value={toonInput}
              onChange={handleToonChange}
              placeholder='Paste or edit TOON here...\n\nExample:\nname: John\nage: 30'
              spellCheck={false}
            />
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="stats">
            <div className="stat-card">
              <div className="stat-value">{stats.jsonTokens.toLocaleString()}</div>
              <div className="stat-label">JSON Tokens</div>
            </div>
            <div className="stat-arrow">→</div>
            <div className="stat-card highlight">
              <div className="stat-value">{stats.toonTokens.toLocaleString()}</div>
              <div className="stat-label">TOON Tokens</div>
            </div>
            <div className="stat-card success">
              <div className="stat-value">{stats.savingsPercent}%</div>
              <div className="stat-label">Tokens Saved</div>
            </div>
          </div>
        )}

      </main>

      <Features />
      <HowToUse />
      <FAQ />

      {/* About Section */}
      <section className="about-section">
        <div className="about-container">
          <div className="info-section">
            <div className="info-card">
              <h3>⚡ What is TOON?</h3>
              <p>
                TOON (Token-Oriented Object Notation) is a lightweight data format that eliminates
                JSON's redundant syntax—like extra braces, brackets, and quotes—while preserving
                your data structure through simple indentation.
              </p>
            </div>

            <div className="info-card">
              <h3>💰 Why Use It?</h3>
              <p>
                Save 30-60% on LLM API costs when working with GPT-4, Claude, Gemini, or other
                token-based models. Perfect for RAG systems, chatbots, and AI agents processing
                large amounts of structured data.
              </p>
            </div>

            <div className="info-card">
              <h3>🔒 100% Private</h3>
              <p>
                All conversion happens locally in your browser. No data is sent to any server.
                Your JSON stays on your machine, ensuring complete privacy and security.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <nav className="footer-links" aria-label="Learn about TOON">
          <Link to="/what-is-toon">What is TOON</Link>
          <Link to="/json-vs-toon">JSON vs TOON</Link>
          <Link to="/toon-format-spec">Format Spec</Link>
          <Link to="/reduce-llm-token-costs">Reduce LLM Costs</Link>
        </nav>
        <p>
          Built for developers who care about performance and cost optimization
        </p>
      </footer>
    </div>
  );
}

export default HomePage;
